// src/HomePage.jsx
import React, { useEffect, useState, useRef, useContext } from "react";
import {feedFailure} from "./utils/feedError";
import api from "./utils/api"; 
import Sidebar from "./Sidebar";
import BottomNav from "./BottomNav";
import RightPanel from "./RightPanel";
import MobileHeader from "./MobileHeader"; 
import FeedItemCard from "./FeedItemCard";
import LoginModal from "./LoginModal"; // <-- Imported the unified modal component
import AppDownloadPromo from "./components/AppDownloadPromo";
import {feedCacheKey,decodeFeedSnapshot,encodeFeedSnapshot,feedPreviewUrls,PAGE_SIZE} from "./utils/feedSnapshot";

// Connect layout engine to authorization and custom hooks tracks
import { AuthContext } from "./contexts/AuthContext";
import { useNotificationCheck } from "./hooks/useNotificationCheck";

function cachedFeed(scope) { try { return decodeFeedSnapshot(localStorage.getItem(feedCacheKey(scope)),scope); } catch { return null; } }

const GUEST_FEED_SESSION_KEY = "recettes_guest_feed_session";

function guestFeedSessionId() {
  try {
    const existing = localStorage.getItem(GUEST_FEED_SESSION_KEY);
    if (existing) return existing;
    const created = window.crypto?.randomUUID?.() || `guest-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(GUEST_FEED_SESSION_KEY, created);
    return created;
  } catch {
    return `guest-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

export default function HomePage() {
  const { user, accessToken } = useContext(AuthContext);
  const scope = user?.user_uuid || 'guest';
  const guestSessionRef = useRef(null);
  if (!guestSessionRef.current) guestSessionRef.current = guestFeedSessionId();
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const inFlight = useRef(new Set());
  const [snapshot] = useState(() => scope === "guest" ? null : cachedFeed(scope));
  const [items, setItems] = useState(snapshot?.items || []);
  const [searchQuery, setSearchQuery] = useState("");
  const [tab] = useState("posts");

  // Auth & Interception States
  const [showLoginModal, setShowLoginModal] = useState(false); // <-- Global modal trigger state

  // Pagination
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(snapshot?.hasMore ?? true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!snapshot);
  const [feedError, setFeedError] = useState("");

  const observerTarget = useRef(null);
  const isSearching = searchQuery.trim() !== "";

  // Connect authentication state checks
  const isLoggedIn = !!accessToken || !!user;

  const markItemsViewed = (feedItems = []) => {
    if (!isLoggedIn || feedItems.length === 0) return;
    const postUuids = [...new Set(feedItems.map((item) => item.post_uuid).filter(Boolean))];
    if (postUuids.length === 0) return;
    api.post("/feed/viewed", { post_uuids: postUuids }).catch((err) => {
      console.error("Failed to mark feed items viewed:", err);
    });
  };

  // Initialize the background notification polling cycle handler natively
  useNotificationCheck(isLoggedIn);

  const fetchData = async (pageToLoad = 1, append = false) => {
    const requestScope=scope;
    const requestKey=requestScope+':'+pageToLoad;
    if(inFlight.current.has(requestKey))return;
    inFlight.current.add(requestKey);
    try {
      if (pageToLoad === 1 && !append) setInitialLoading(true);
      else setLoadingMore(true);
      setFeedError("");

      const res = await api.get("/feed", { 
        params: {
          page: pageToLoad,
          page_size: PAGE_SIZE,
          filter_type: tab,
          ...(requestScope === "guest" ? { session_id: guestSessionRef.current } : {}),
        }
      });
      
      if(scopeRef.current!==requestScope)return;
      const rawItems = res.data?.items || [];
      const backendHasMore = res.data?.pagination?.has_more ?? false;
      markItemsViewed(rawItems);
      if(!append&&pageToLoad===1) {
        try {
          if (requestScope === "guest") {
            localStorage.removeItem(feedCacheKey(requestScope));
          } else {
            const saved=encodeFeedSnapshot(rawItems,backendHasMore,requestScope);
            if(saved)localStorage.setItem(feedCacheKey(requestScope),saved);
            else localStorage.removeItem(feedCacheKey(requestScope));
          }
        } catch {}
        for(const url of feedPreviewUrls(rawItems)){const image=new Image();image.src=url;}
      }

    setItems((prev) => {
  if (!append) return rawItems;

  const newItems = rawItems.filter(
    (newItem) =>
      !prev.some(
        (oldItem) =>
          (oldItem.post_uuid || oldItem.item_uuid || oldItem.id) ===
          (newItem.post_uuid || newItem.item_uuid || newItem.id)
      )
  );

  return [...prev, ...newItems];
});
      setHasMore(backendHasMore);
      setPage(pageToLoad);
    } catch (err) {
      if(scopeRef.current!==requestScope)return;
      const failure=feedFailure(err);
      console.error("Unified feed fetch failed:", failure);
      setFeedError(failure.message);
      setHasMore(false);
    } finally {
      inFlight.current.delete(requestKey);
      if(scopeRef.current===requestScope){setInitialLoading(false);setLoadingMore(false);}
    }
  };

  useEffect(() => {
    const saved=scope === "guest" ? null : cachedFeed(scope);
    setItems(saved?.items||[]);
    setPage(1);
    setHasMore(saved?.hasMore??true);
    if (!isSearching) fetchData(1, false);
  }, [isSearching, tab, scope]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (isSearching) {
        api.get("/search", { params: { q: searchQuery, type: tab } })
          .then(({ data }) => {
            const results = Array.isArray(data) ? data : data.results || [];
            setItems(results);
            setHasMore(false);
          })
          .catch((err) => console.error("Search failed:", err));
      }
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchQuery, isSearching, tab]);

  useEffect(() => {
    if (isSearching || !hasMore || initialLoading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore) {
          fetchData(page + 1, true);
        }
      },
      { threshold: 0.5 }
    );
    if (observerTarget.current) observer.observe(observerTarget.current);
    return () => observer.disconnect();
  }, [page, hasMore, loadingMore, initialLoading, isSearching]);

  return (
    <div className="flex flex-col min-h-screen w-full bg-white font-sans selection:bg-orange-100">
      {/* Global Mobile Header - Shows on viewports below 768px wide */}
      <div className="md:hidden shrink-0 z-50 sticky top-0">
        <MobileHeader />
      </div>

      {/* Main Content Area Container Track */}
      <div className="flex flex-1 flex-row min-w-0">
        
        {/* Persistent desktop navigation */}
        <aside className="hidden md:block w-[240px] border-r border-gray-200 min-h-screen shrink-0 sticky top-0">
          <Sidebar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        </aside>

        {/* Primary Content View Pane - Holds Middle Feed Cards and Right Sidebar panels together */}
        <div className="flex flex-1 justify-center gap-8 w-full max-w-[1140px] mx-auto min-w-0 px-6">
          
          {/* Scrollable Main Cards Stream */}
          <main className="w-full max-w-[620px] pt-6 pb-24 min-w-0">
            <div className="space-y-8">
              <div className="md:hidden">
                <AppDownloadPromo compact />
              </div>
              {initialLoading && items.length === 0 ? (
                 <div className="space-y-8">
                   {[1,2,3].map(i => <div key={i} className="h-64 bg-gray-50 rounded-3xl animate-pulse" />)}
                 </div>
              ) : items.length === 0 ? (
                <div className="text-center py-16 px-6 border border-gray-100 rounded-2xl bg-white shadow-sm">
                  <p className="text-sm font-bold text-gray-500 uppercase tracking-wider">
                    {feedError || "You're caught up."}
                  </p>
                  <p className="mt-2 text-sm text-gray-400">
                    {feedError ? "You can retry without reloading the page." : "Share something delicious and help freshen up the feed."}
                  </p>
                  {feedError ? <button type="button" onClick={() => fetchData(1,false)} className="inline-flex mt-5 px-5 py-2 rounded-full bg-orange-500 text-white text-sm font-bold">Try again</button> : <a
                    href="/create"
                    className="inline-flex mt-5 px-5 py-2 rounded-full bg-orange-500 text-white text-sm font-bold hover:bg-orange-600 transition"
                  >
                    Post a recipe
                  </a>}
                </div>
              ) : (
                items.map((item, index) => (
                  <FeedItemCard 
                    key={item.post_uuid || item.item_uuid || item.id}
                    item={item} priority={index === 0}
                    onRequireLogin={() => setShowLoginModal(true)} // <-- Passed callback prop down to child card
                  />
                ))
              )}

              {/* Infinite Scroll Observer Tracking Element */}
              <div ref={observerTarget} className="h-20 flex items-center justify-center">
                {loadingMore && <div className="h-5 w-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />}
                {!hasMore && items.length > 0 && (
                  <p className="text-xs font-bold text-gray-300 uppercase tracking-widest">
                    End of feed
                  </p>
                )}
              </div>
            </div>
          </main>

          {/* Right Information Aside Panel */}
          <aside className="hidden lg:block w-[280px] shrink-0 pt-6 h-fit sticky top-6 border-l border-gray-100 pl-6">
            <RightPanel />
          </aside>
        </div>

      </div>

      {/* Navigation Footer Deck - Small Touch viewports only */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-t border-gray-100 z-50">
        <BottomNav />
      </div>

      {/* Global Interactive Intercept Modal Context */}
      <LoginModal open={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </div>
  );
}



